import type { JSX } from 'react';
import type { CreateModelBody } from '@voxler/contracts';
import { modelFeatureSchema, providerSchema, tariffCurrencySchema } from '@voxler/contracts';
import { assertNever, defaultCurrencyOf } from '@voxler/domain';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useToast } from '../../components/toast';
import { internalSend, validationDetail } from '../../lib/api';
import { CURRENCY_LABELS, type TariffCurrency } from '../../lib/tariffs';
import type { Model, ModelFeature, Provider } from '../../lib/use-cases';
import { Callout } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHead,
} from '@/components/ui/dialog';
import { CheckRow, Field, FieldHint, Fields2, Fieldset } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/native-select';

/** French label of each capability, shown with its technical key. */
export const FEATURE_LABELS: Record<ModelFeature, string> = {
  text: 'Texte',
  pdf_input: 'PDF en entrée',
  image_input: 'Image en entrée',
  image_output: 'Image en sortie',
  structured_output: 'Sortie structurée',
  batch: 'Traitement par lots',
  sms: 'SMS',
};

/**
 * Features of a new catalog entry, derived from its provider (lot 17): the API
 * refuses an incoherent pair, and a use case picks its model by feature (an SMS
 * use case needs an `sms` service). AI models keep the single `text` feature.
 */
const FEATURES_BY_PROVIDER: Record<Provider, ModelFeature[]> = {
  anthropic: ['text'],
  google: ['text'],
  vonage: ['sms'],
};

/**
 * Capabilities a catalog entry can carry. A Vonage service is exactly `sms` and
 * an AI model never carries it (the API refuses the other pairs).
 */
function selectableFeatures(provider: Provider): readonly ModelFeature[] {
  switch (provider) {
    case 'anthropic':
    case 'google':
      return modelFeatureSchema.options.filter((feature) => feature !== 'sms');
    case 'vonage':
      return FEATURES_BY_PROVIDER.vonage;
    default:
      return assertNever(provider);
  }
}

const MODEL_ID_PLACEHOLDERS: Record<Provider, string> = {
  anthropic: 'claude-sonnet-4-6',
  google: 'gemini-2.5-flash',
  vonage: 'sms',
};

const DISPLAY_NAME_PLACEHOLDERS: Record<Provider, string> = {
  anthropic: 'Claude Sonnet 4.6',
  google: 'Gemini 2.5 Flash',
  vonage: 'SMS (Vonage)',
};

/** `fieldset` « Capacités » : one `check-row` per capability the provider allows. */
function FeaturePicker({
  provider,
  value,
  onChange,
}: {
  provider: Provider;
  value: readonly ModelFeature[];
  onChange: (features: ModelFeature[]) => void;
}): JSX.Element {
  const locked = provider === 'vonage';
  return (
    <Fieldset title="Capacités">
      <Fields2 className="gap-x-4 gap-y-2.5">
        {selectableFeatures(provider).map((feature) => (
          <CheckRow
            key={feature}
            label={FEATURE_LABELS[feature]}
            code={feature}
            checked={value.includes(feature)}
            disabled={locked}
            onChange={(event) => {
              onChange(
                event.target.checked
                  ? [...value, feature]
                  : value.filter((item) => item !== feature),
              );
            }}
          />
        ))}
      </Fields2>
      {locked ? <FieldHint>Un service Vonage porte uniquement la capacité sms.</FieldHint> : null}
    </Fieldset>
  );
}

/**
 * « Ajouter un modèle » (ModelDialog.dc.html). Stays mounted: what was typed is
 * kept when the dialog is closed, and cleared after a successful creation.
 */
export function AddModelDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  /** Called with the new entry: the page opens its tariffs right away. */
  onCreated: (model: Model) => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const [provider, setProvider] = useState<Provider>('anthropic');
  const [providerModelId, setProviderModelId] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [features, setFeatures] = useState<ModelFeature[]>(FEATURES_BY_PROVIDER.anthropic);
  const [currency, setCurrency] = useState<TariffCurrency>(defaultCurrencyOf('anthropic'));
  const [idError, setIdError] = useState('');
  const [modelError, setModelError] = useState('');

  const close = (): void => {
    setIdError('');
    setModelError('');
    onClose();
  };

  const createModel = useMutation({
    mutationFn: (body: CreateModelBody) => internalSend<Model>('POST', '/internal/models', body),
    onSuccess: (created) => {
      setProvider('anthropic');
      setFeatures(FEATURES_BY_PROVIDER.anthropic);
      setCurrency(defaultCurrencyOf('anthropic'));
      setProviderModelId('');
      setDisplayName('');
      close();
      void queryClient.invalidateQueries({ queryKey: ['models'] });
      notify('Modèle ajouté : saisissez son tarif');
      // Without a tariff in effect every request on it fails: go straight to it.
      onCreated(created);
    },
    onError: (error) => {
      setModelError(validationDetail(error) ?? 'Échec de l’ajout du modèle.');
    },
  });

  const submit = (): void => {
    setIdError('');
    setModelError('');
    const id = providerModelId.trim();
    if (id.length === 0) {
      setIdError('Identifiant requis');
      return;
    }
    const name = displayName.trim();
    createModel.mutate({
      provider,
      providerModelId: id,
      displayName: name.length > 0 ? name : id,
      features,
      currency,
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
      }}
    >
      <DialogContent width={600}>
        <DialogHead
          title="Ajouter un modèle"
          description="Référencer un modèle d’IA ou un service Vonage (SMS)."
        />
        <form
          id="add-model-form"
          className="contents"
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          <DialogBody>
            <Fieldset title="Modèle">
              <Field label="Fournisseur" required>
                <NativeSelect
                  value={provider}
                  onChange={(event) => {
                    const parsed = providerSchema.safeParse(event.target.value);
                    if (parsed.success) {
                      setProvider(parsed.data);
                      setFeatures(FEATURES_BY_PROVIDER[parsed.data]);
                      setCurrency(defaultCurrencyOf(parsed.data));
                      setIdError('');
                      setModelError('');
                    }
                  }}
                >
                  {providerSchema.options.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              <Fields2>
                <Field label="Identifiant chez le fournisseur" required error={idError}>
                  <Input
                    className="font-mono"
                    value={providerModelId}
                    onChange={(event) => {
                      setProviderModelId(event.target.value);
                      setIdError('');
                      setModelError('');
                    }}
                    placeholder={MODEL_ID_PLACEHOLDERS[provider]}
                  />
                </Field>
                <Field
                  label="Nom affiché"
                  hint="Nom montré dans les listes. Vide : l’identifiant est affiché."
                >
                  <Input
                    value={displayName}
                    onChange={(event) => {
                      setDisplayName(event.target.value);
                    }}
                    placeholder={DISPLAY_NAME_PLACEHOLDERS[provider]}
                  />
                </Field>
              </Fields2>
            </Fieldset>

            <FeaturePicker provider={provider} value={features} onChange={setFeatures} />

            <Field
              label="Devise des tarifs"
              hint="Devise des prix d’achat facturés par le fournisseur, fixée à la création : toutes les versions de tarif l’utilisent."
            >
              <NativeSelect
                value={currency}
                onChange={(event) => {
                  const parsed = tariffCurrencySchema.safeParse(event.target.value);
                  if (parsed.success) {
                    setCurrency(parsed.data);
                  }
                }}
              >
                {tariffCurrencySchema.options.map((option) => (
                  <option key={option} value={option}>
                    {CURRENCY_LABELS[option]}
                  </option>
                ))}
              </NativeSelect>
            </Field>

            {modelError.length > 0 ? <Callout variant="destructive">{modelError}</Callout> : null}
          </DialogBody>
        </form>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={close}>
            Annuler
          </Button>
          <Button type="submit" form="add-model-form" size="lg" disabled={createModel.isPending}>
            Ajouter le modèle
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** « Modifier les capacités » of a catalog entry. Mounted only while open: it starts from the entry's features. */
export function EditFeaturesDialog({
  model,
  onClose,
}: {
  model: Model;
  onClose: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const [features, setFeatures] = useState<ModelFeature[]>(model.features);
  const [error, setError] = useState('');

  const saveFeatures = useMutation({
    mutationFn: (input: { id: string; features: ModelFeature[] }) =>
      internalSend<Model>('PATCH', `/internal/models/${input.id}`, { features: input.features }),
    onSuccess: () => {
      onClose();
      void queryClient.invalidateQueries({ queryKey: ['models'] });
      notify('Capacités mises à jour');
    },
    onError: (failure) => {
      setError(validationDetail(failure) ?? 'Échec de la mise à jour.');
    },
  });

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent width={600}>
        <DialogHead title="Modifier les capacités" description={model.displayName} />
        <DialogBody>
          <FeaturePicker provider={model.provider} value={features} onChange={setFeatures} />
          {error.length > 0 ? <Callout variant="destructive">{error}</Callout> : null}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button
            type="button"
            size="lg"
            onClick={() => {
              setError('');
              saveFeatures.mutate({ id: model.id, features });
            }}
            disabled={saveFeatures.isPending}
          >
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
